import { chartAssets } from '../constants/assets'
import { revenueValueToY } from '../utils/chartMath'
import type { DesignFrame } from '../utils/geometry'

/** Design-space size of the Revenue Overview card in Figma. */
export const revenueFrame: DesignFrame = {
  width: 471,
  height: 361,
}

export const revenueRanges = [
  'Monthly',
  'Weekly',
  'Yearly',
] as const

export type RevenueRange =
  (typeof revenueRanges)[number]

export type RevenueDataPoint = {
  id: string
  label: string
  period: string

  /** Revenue in thousands of EUR. */
  valueK: number

  x: number
}

const monthlyX = [
  76,
  134,
  207,
  277,
  347,
  418,
] as const

/* =========================================================
   DYNAMIC LAST 6 MONTHS

   Example:
   August 2026:
   Mar, Apr, May, Jun, Jul, Aug

   September 2026:
   Apr, May, Jun, Jul, Aug, Sep
========================================================= */

export const getCurrentRevenueMonths = () => {
  const now = new Date()

  const xLabels: string[] = []
  const periods: string[] = []

  for (let offset = 5; offset >= 0; offset--) {
    const date = new Date(
      now.getFullYear(),
      now.getMonth() - offset,
      1,
    )

    xLabels.push(
      date.toLocaleString('en-US', {
        month: 'short',
      }),
    )

    periods.push(
      date.toLocaleString('en-US', {
        month: 'short',
        year: 'numeric',
      }),
    )
  }

  return {
    xLabels,
    periods,
  }
}

const currentRevenueMonths =
  getCurrentRevenueMonths()

export const revenueMonths =
  currentRevenueMonths.xLabels

export const revenueMonthPeriods =
  currentRevenueMonths.periods

export const revenueWeeks = [
  'W1',
  'W2',
  'W3',
  'W4',
  'W5',
  'W6',
] as const

export const revenueYears = [
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
  '2026',
] as const

export const revenueAxisLabels = [
  '100k',
  '75k',
  '50k',
  '25k',
  '0',
] as const

export const revenuePlotConfig: Record<
  RevenueRange,
  {
    maxK: number
    axisLabels: readonly string[]
  }
> = {
  Monthly: {
    maxK: 100,
    axisLabels: revenueAxisLabels,
  },

  Weekly: {
    maxK: 50,
    axisLabels: [
      '50k',
      '37k',
      '25k',
      '12k',
      '0',
    ],
  },

  Yearly: {
    maxK: 1000,
    axisLabels: [
      '1000k',
      '750k',
      '500k',
      '250k',
      '0',
    ],
  },
}

/**
 * Horizontal gridlines plus the darker baseline,
 * in design-space px.
 */
export const revenueGridLines = [
  {
    id: 'grid-1',
    x: 58,
    y: 123,
    width: 380,
    src: chartAssets.gridLine,
  },
  {
    id: 'grid-2',
    x: 58,
    y: 171,
    width: 380,
    src: chartAssets.gridLine,
  },
  {
    id: 'grid-3',
    x: 58,
    y: 219,
    width: 380,
    src: chartAssets.gridLine,
  },
  {
    id: 'grid-4',
    x: 58,
    y: 267,
    width: 380,
    src: chartAssets.gridLine,
  },
  {
    id: 'baseline',
    x: 52,
    y: 315,
    width: 392,
    src: chartAssets.axisLine,
  },
]

const buildPoints = (
  labels: readonly string[],
  periods: readonly string[],
  valuesK: readonly number[],
): RevenueDataPoint[] =>
  labels.map((label, index) => ({
    id: `${label}-${index}`,

    label,

    period:
      periods[index] ?? label,

    valueK:
      valuesK[index] ?? 0,

    x:
      monthlyX[index] ??
      monthlyX[monthlyX.length - 1],
  }))

/**
 * Numeric series per range.
 * Y positions are derived from valueK.
 */
export const revenueDatasets: Record<
  RevenueRange,
  {
    points: RevenueDataPoint[]
    xLabels: readonly string[]
  }
> = {
  Monthly: {
    xLabels: revenueMonths,

    points: buildPoints(
      revenueMonths,

      revenueMonthPeriods,

      [32, 45, 65, 60, 100, 92],
    ),
  },

  Weekly: {
    xLabels: revenueWeeks,

    points: buildPoints(
      revenueWeeks,

      [
        'Week 1',
        'Week 2',
        'Week 3',
        'Week 4',
        'Week 5',
        'Week 6',
      ],

      [18, 24, 31, 28, 38, 35],
    ),
  },

  Yearly: {
    xLabels: revenueYears,

    points: buildPoints(
      revenueYears,

      [
        '2021',
        '2022',
        '2023',
        '2024',
        '2025',
        '2026',
      ],

      [420, 510, 580, 640, 720, 850],
    ),
  },
}

/*
  Six points:

  0 = oldest month
  1
  2
  3
  4
  5 = CURRENT MONTH

  So current month is selected by default.
*/
export const revenueDefaultActiveIndex = 5

export const revenuePlotPoints = (
  range: RevenueRange,
) => {
  const { maxK } =
    revenuePlotConfig[range]

  return revenueDatasets[
    range
  ].points.map((point) => ({
    ...point,

    y: revenueValueToY(
      point.valueK,
      maxK,
    ),
  }))
}

/**
 * Build plot points from an API/mock revenue
 * series (dashboard overview).
 */
export const revenuePlotPointsFromSeries = (
  valuesK: readonly number[],
  labels: readonly string[],
  periods: readonly string[],
  maxK: number,
) =>
  labels.map((label, index) => {
    const valueK =
      valuesK[index] ?? 0

    return {
      id: `${label}-${index}`,

      label,

      period:
        periods[index] ?? label,

      valueK,

      x:
        monthlyX[index] ??
        monthlyX[
          monthlyX.length - 1
        ],

      y: revenueValueToY(
        valueK,
        maxK,
      ),
    }
  })

/**
 * X-axis tick positions.
 * Slightly offset per month in Figma.
 */
export const revenueAxisTicks = [
  {
    x: 76,
    lit: false,
  },
  {
    x: 134,
    lit: false,
  },
  {
    x: 207,
    lit: false,
  },
  {
    x: 280,
    lit: false,
  },
  {
    x: 349,
    lit: true,
  },
  {
    x: 425,
    lit: true,
  },
] as const

export const revenueLineColor =
  '#A3B70D'